import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const ConflictResolutionDialog = ({ open, onOpenChange, conflictData, onApply }) => {
  const [resolutions, setResolutions] = useState({});

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setResolutions({});
    }
    onOpenChange(isOpen);
  };

  const handleApply = () => {
    onApply(resolutions);
    setResolutions({});
  };

  const allResolved = conflictData?.conflicts?.every(c => resolutions[c.field]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="conflict-dialog-title">
            <span className="text-2xl">⚡</span>
            Flight Data Conflicts Detected
          </DialogTitle>
          <DialogDescription>
            Some existing flight data differs from the AeroDataBox API.
            Choose which value to keep for each field below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Show safe updates summary */}
          {conflictData && Object.keys(conflictData.safe_updates).length > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm font-medium text-green-900 mb-1">
                {Object.keys(conflictData.safe_updates).length} fields will be added automatically
              </div>
              <div className="text-xs text-green-700">
                (Aircraft details, distances, flight times, etc.)
              </div>
            </div>
          )}

          {/* Show conflicts */}
          {conflictData?.conflicts.map((conflict) => (
            <div key={conflict.field} className="border rounded-lg p-4 bg-white" data-testid={`conflict-field-${conflict.field}`}>
              <div className="font-medium text-gray-900 mb-3">{conflict.display_name}</div>
              <div className="grid grid-cols-2 gap-3">
                {/* Keep Existing Option */}
                <button
                  onClick={() => setResolutions({ ...resolutions, [conflict.field]: 'keep_existing' })}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    resolutions[conflict.field] === 'keep_existing'
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`conflict-${conflict.field}-keep`}
                >
                  <div className="text-xs text-gray-600 mb-1 font-medium">YOUR DATA</div>
                  <div className="text-sm font-semibold text-gray-900">{conflict.existing_value}</div>
                </button>

                {/* Use API Option */}
                <button
                  onClick={() => setResolutions({ ...resolutions, [conflict.field]: 'use_api' })}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    resolutions[conflict.field] === 'use_api'
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                  data-testid={`conflict-${conflict.field}-api`}
                >
                  <div className="text-xs text-gray-600 mb-1 font-medium">AERODATABOX API</div>
                  <div className="text-sm font-semibold text-gray-900">{conflict.api_value}</div>
                </button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            data-testid="conflict-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!allResolved}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="conflict-apply-btn"
          >
            Apply Selected Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
